import { useEffect, useState } from "react";

import { ArrowPathIcon, MagnifyingGlassIcon, PencilIcon, TrashIcon } from "@heroicons/react/24/outline";

import LoadingWheel from "../../components/shared/LoadingWheel";
import { usePageNameContext } from "../../contexts/PageTitleContext";
import useCategories from "../../data/Category/useCategories";
import { IPartCategory } from "../../models/Category/Category";
import { Button } from "../../components/base/Button";
import { Table } from "../../components/base/Table/Table";
import { CategoryQueryKeys } from "../../data/Category/keys";
import { createColumnHelper } from "@tanstack/react-table";
import { useNavigate } from "react-router-dom";

import CategoryModal from "../../components/CategoryEditor/CategoryModal";

import ConfirmModal from "../../components/shared/Confirm/ConfirmModal";
import useCategoryMutations from "../../data/Category/useCategoryMutations";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";

// import CategoryTable from "../../components/categoryEditor/CategoryTable";
// import CategoryModal from "../../components/categoryEditor/CategoryModal";

export default function Categories() {
  const { setInfo } = usePageNameContext();
  const navigate = useNavigate();
  const { remove, pushPartProTrade } = useCategoryMutations();
  const { deleteHandlers } = useDefaultCRUDHandlers("Category");
  const [category, setCategory] = useState<IPartCategory | undefined>(undefined);
  const [createOpen, setCreateOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteId, setDeleteId] = useState("");

  const colHelper = createColumnHelper<IPartCategory>();

  useEffect(() => {
    setInfo({
      name: "Category Editor",
      desc: "View and edit your categories here",
      pageTitle: "Categories",
    });
  }, []);


  const cols = [
    colHelper.accessor("id", { header: "Category ID" }),
    colHelper.accessor("name", { header: "Category Name" }),
    // colHelper.accessor("domain", { header: "Domain" }),
    colHelper.display({
      header: "Actions",
      cell: (info) => (
        <div className="flex items-center space-x-4">
          <PencilIcon
            className="w-5 h-5 opacity-70 hover:opacity-100 hover:cursor-pointer"
            onClick={() => navigate(`/categories/${info.row.original.id}`)}
          />
          <TrashIcon
            className="w-5 h-5 opacity-70 hover:opacity-100 hover:cursor-pointer"
            onClick={() => { setDeleteId(info.row.original.id); setDeleteOpen(true); }}
          />
        </div>
      ),
    }),
  ];


  const handleDeleteCategory = () => {
    if (!deleteId) return;
    remove.mutate(deleteId, {
      ...deleteHandlers,
      onSuccess: () => {
        deleteHandlers.onSuccess();
        setDeleteOpen(false);
      },
    });
  }

  const handleAddCategory = () => {
    setCategory(undefined);
    navigate(`/categories/new`);
  }

  return (
    <div className="flex flex-col w-full h-full gap-4">

      <CategoryModal open={createOpen} setOpen={setCreateOpen} categoryId={category?.id} />

      <ConfirmModal
        open={deleteOpen} setOpen={setDeleteOpen} title={"Confirm Deletion"}
        message={`Are you sure you want to delete this category?\n This might cause issues, but it is a soft delete.`}
        confirmButtonText={"Delete"}
        onConfirm={handleDeleteCategory}
        isLoading={false}
      />

      <div className="flex justify-between">
        <div className="flex gap-2">
          <Button onClick={() => navigate("/category-groups")} variant="outlined">
            Category Groups
          </Button>
          <Button
            onClick={handleAddCategory}
          >
            Add Category
          </Button>
        </div>
        <Button
          variant="outlined"
          className="flex items-center space-x-2"
          onClick={() => pushPartProTrade.mutate()}
          disabled={pushPartProTrade.isPending}
        >
          <ArrowPathIcon className={`h-5 w-5 -ml-1 ${pushPartProTrade.isPending ? "animate-spin" : ""}`} />
          <p>Sync PartPro Trade</p>
        </Button>
      </div>
      <Table
        url="/part/categories"
        queryKey={CategoryQueryKeys.all()}
        columns={cols}
        searchLabel="Search by name or ID"
      />
    </div>
  );
}
